<template>
  <div class="agent-board" :class="{ 'agent-board--dark': isDarkSkin }">
    <div v-if="loading" class="agent-board__skeleton">
      <b-skeleton v-for="n in 6" :key="n" height="42px" class="mb-50"/>
    </div>

    <div v-else-if="!rows.length" class="agent-board__empty">
      <i class="fa-solid fa-user-slash"></i>
      <span>{{ $t('salesDashboard.noData') }}</span>
    </div>

    <div v-else class="agent-board__list">
      <button
          v-for="(a, i) in rows"
          :key="a.userId || i"
          type="button"
          class="agent-row"
          @click="$emit('select', { value: a.userId, label: a.name })"
      >
        <span class="agent-row__rank" :class="rankClass(i)">
          <i v-if="i < 3" class="fa-solid fa-medal"></i>
          <template v-else>{{ i + 1 }}</template>
        </span>

        <span class="agent-row__avatar">{{ initials(a.name) }}</span>

        <span class="agent-row__main">
          <span class="agent-row__nameline">
            <span class="agent-row__name">{{ a.name }}</span>
            <span v-if="roleLetter(a)" class="agent-row__role" :class="'is-' + roleLetter(a).toLowerCase()"
                  v-b-tooltip.hover :title="roleTitle(a)">{{ roleLetter(a) }}</span>
          </span>
          <span class="agent-row__bar">
            <span class="agent-row__bar-fill" :style="{ width: barWidth(a) }"></span>
          </span>
          <template v-if="metric !== 'ftd'">
            <span v-if="a.role === 'SALE'" class="agent-row__funnel">
              {{ $t('salesDashboard.leads') }}
              <strong class="agent-row__leads-link" role="button" tabindex="0"
                      v-b-tooltip.hover :title="$t('salesDashboard.receivedLeadsTooltip')"
                      @click.stop="emitLeads(a, 'SALES')"
                      @keydown.enter.stop.prevent="emitLeads(a, 'SALES')">{{ leadsVal(a) }}</strong>
              <i class="fa-solid fa-arrow-right-long mx-25"></i>
              {{ $t('salesDashboard.ftd') }} <strong>{{ a.salesFtd || 0 }}</strong>
              <span v-if="leadsVal(a)" class="agent-row__conv">({{ convPct(a) }}%)</span>
            </span>
            <span v-else-if="a.role === 'RETENTION'" class="agent-row__funnel">
              <!-- Retention agents don't make FTDs — only their STD count matters here -->
              {{ $t('salesDashboard.std') }} <strong>{{ a.retStd || 0 }}</strong>
              <!-- clients handed to this retention agent in the period (backend receivedCount) -->
              <span v-if="a.receivedCount != null" class="agent-row__leads-link agent-row__received"
                    role="button" tabindex="0"
                    v-b-tooltip.hover :title="$t('salesDashboard.receivedClientsTooltip')"
                    @click.stop="emitLeads(a, 'RETENTION')"
                    @keydown.enter.stop.prevent="emitLeads(a, 'RETENTION')">
                <i class="fa-solid fa-user-plus mr-25"></i>{{ a.receivedCount }} {{ $t('salesDashboard.received') }}
              </span>
            </span>
          </template>
        </span>

        <span class="agent-row__metrics">
          <!-- Sales table: the FTD COUNT is the star — big gold pill; money is secondary -->
          <template v-if="metric === 'ftd'">
            <span class="agent-row__ftd-hero" v-b-tooltip.hover :title="$t('salesDashboard.ftd')">
              {{ a.salesFtd || 0 }}<small>{{ $t('salesDashboard.ftd') }}</small>
            </span>
            <span class="agent-row__money-sub">{{ primaryText(a) }}</span>
            <span class="agent-row__sub">
              <span class="agent-row__leads-link" role="button" tabindex="0"
                    v-b-tooltip.hover :title="$t('salesDashboard.receivedLeadsTooltip')"
                    @click.stop="emitLeads(a, 'SALES')"
                    @keydown.enter.stop.prevent="emitLeads(a, 'SALES')">
                {{ leadsVal(a) }} {{ $t('salesDashboard.leads') }}
              </span>
            </span>
          </template>
          <template v-else>
            <span class="agent-row__deposit">{{ primaryText(a) }}</span>
            <span v-if="a.withdraw" class="agent-row__out">-{{ convertToEURO(Math.abs(a.withdraw)) }}</span>
            <span class="agent-row__sub">{{ subText(a) }}</span>
            <!-- the agent's FAKE (non-FTD) deposit money in range — retention counterpart of the sales fake pill -->
            <span v-if="showFake" class="agent-row__fake-sub" :class="{ 'is-zero': !a.fakeDeposit }" v-b-tooltip.hover
                  :title="$t('Fake Deposits') + ': ' + (a.fakeDepositCount || 0)">
              FAKE {{ convertToEURO(a.fakeDeposit || 0) }}
            </span>
          </template>
        </span>

        <i class="fa-solid fa-chevron-right agent-row__chevron"></i>
      </button>
    </div>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'TopAgentsLeaderboard',
  mixins: [mixinBase],
  props: {
    agents: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    isDarkSkin: { type: Boolean, default: false },
    // Show all agents by default (the list scrolls); pass a number to cap it.
    limit: { type: Number, default: 0 },
    // 'deposit' = rank by deposit € (retention/processors); 'ftd' = rank by FTD
    // conversions (sales agents, who never process deposits).
    metric: { type: String, default: 'deposit' },
    // Dashboard-level FAKE switch: hides the retention table's fake amount line.
    showFake: { type: Boolean, default: true },
  },
  computed: {
    rows() {
      // Drop the "no user" bucket (no/unknown agent on the client/deposit).
      // Users who work both sales and retention appear in both tables when they
      // have activity in each — membership is by activity, not by role.
      // `.filter()` returns a fresh array, so sorting it never mutates the
      // `agents` prop (used elsewhere by the dashboard totals).
      let list = (this.agents || []).filter(a => a.userId && a.name !== 'No User');
      if (this.metric === 'ftd') {
        // Sales table: FTD conversions rule the ranking; FTD money breaks ties.
        // The retention table (metric 'deposit') keeps the backend order.
        list = list.sort((x, y) =>
            ((y.salesFtd || 0) - (x.salesFtd || 0)) || ((y.deposit || 0) - (x.deposit || 0)));
      }
      // limit = 0 → show all (the list scrolls).
      return this.limit > 0 ? list.slice(0, this.limit) : list;
    },
    maxMetric() {
      return this.rows.reduce((m, a) => Math.max(m, this.metricVal(a)), 0) || 1;
    },
    totalMetric() {
      return (this.agents || []).reduce((t, a) => t + this.metricVal(a), 0) || 0;
    },
  },
  methods: {
    metricVal(a) {
      // Sales table ranks (and draws its bars) by FTD COUNT — the star metric;
      // retention keeps ranking by money processed.
      if (this.metric === 'ftd') {
        return a.salesFtd || 0;
      }
      return Math.abs(a.deposit || 0);
    },
    primaryText(a) {
      return this.convertToEURO(a.deposit || 0);
    },
    // Leads/clients received in the period (backend `receivedCount`), falling
    // back to the legacy pending-leads counter until the backend provides it.
    leadsVal(a) {
      return a.receivedCount != null ? a.receivedCount : (a.salesLeads || 0);
    },
    emitLeads(a, kind) {
      if (!a || !a.userId) return;
      this.$emit('leads-click', { userId: a.userId, name: a.name, kind });
    },
    subText(a) {
      return `${a.count} ${this.$t('salesDashboard.depositsCount')} · ${this.share(a)}%`;
    },
    barWidth(a) {
      const pct = Math.round((this.metricVal(a) / this.maxMetric) * 100);
      return Math.max(4, pct) + '%';
    },
    share(a) {
      if (!this.totalMetric) return '0';
      return ((a.deposit || 0) / this.totalMetric * 100).toFixed(1);
    },
    rankClass(i) {
      return ['is-gold', 'is-silver', 'is-bronze'][i] || 'is-plain';
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
    },
    roleLetter(a) {
      if (a.role === 'SALE') return 'S';
      if (a.role === 'RETENTION') return 'R';
      return '';
    },
    roleTitle(a) {
      if (a.role === 'SALE') return this.$t('salesDashboard.salesman');
      if (a.role === 'RETENTION') return this.$t('salesDashboard.retention');
      return '';
    },
    convPct(a) {
      // Conversion over the leads figure actually displayed (receivedCount
      // when the backend provides it, legacy pending leads otherwise).
      const leads = this.leadsVal(a);
      if (!leads) return '0';
      return ((a.salesFtd || 0) / leads * 100).toFixed(0);
    },
  },
};
</script>

<style scoped lang="scss">
.agent-board {
  &__list {
    display: flex; flex-direction: column; gap: 0.35rem;
    max-height: 680px;
    overflow-y: auto;
    scrollbar-width: thin;
    padding-right: 2px;
    &::-webkit-scrollbar { width: 7px; }
    &::-webkit-scrollbar-thumb { background: rgba(0, 123, 255, 0.25); border-radius: 8px; }
    &::-webkit-scrollbar-thumb:hover { background: rgba(0, 123, 255, 0.45); }
  }
  &__empty {
    display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
    padding: 2.5rem 1rem; color: #b9b9c3; font-size: 0.88rem;
    i { font-size: 1.6rem; }
  }
  &__skeleton { padding: 0.25rem 0; }
}

.agent-row {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.55rem 0.65rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;

  &:hover {
    background: rgba(0, 123, 255, 0.06);
    transform: translateX(3px);
    .agent-row__chevron { opacity: 1; transform: translateX(0); }
  }

  &__rank {
    flex-shrink: 0;
    width: 26px; height: 26px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 8px;
    font-size: 0.78rem; font-weight: 700;
    color: #6e6b7b; background: rgba(110, 117, 159, 0.1);
    &.is-gold { color: #fff; background: linear-gradient(135deg, #fbbf24, #f59e0b); }
    &.is-silver { color: #fff; background: linear-gradient(135deg, #cbd5e1, #94a3b8); }
    &.is-bronze { color: #fff; background: linear-gradient(135deg, #d8a07a, #b45309); }
  }

  &__avatar {
    flex-shrink: 0;
    width: 34px; height: 34px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%;
    font-size: 0.74rem; font-weight: 700; color: #fff;
    background: linear-gradient(135deg, #4da3ff, #007BFF);
  }

  &__main { flex: 1; min-width: 0; }
  &__nameline {
    display: flex; align-items: center; gap: 0.4rem;
    margin-bottom: 0.3rem; min-width: 0;
  }
  &__name {
    font-weight: 600; font-size: 0.88rem; color: #1f2a44;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__role {
    flex-shrink: 0;
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 5px;
    font-size: 0.64rem; font-weight: 800; color: #fff; line-height: 1;
    &.is-s { background: linear-gradient(135deg, #34d399, #10b981); }
    &.is-r { background: linear-gradient(135deg, #4da3ff, #007BFF); }
  }
  &__funnel {
    display: block; margin-top: 0.3rem;
    font-size: 0.72rem; color: #6e6b7b; white-space: nowrap;
    strong { color: #1f2a44; font-weight: 700; }
    .agent-row__conv { color: #10b981; font-weight: 700; margin-left: 0.2rem; }
  }
  &__bar {
    display: block; height: 6px; border-radius: 999px;
    background: rgba(110, 117, 159, 0.12); overflow: hidden;
  }
  &__bar-fill {
    display: block; height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #4da3ff, #007BFF);
    transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &__metrics { flex-shrink: 0; text-align: right; }
  &__deposit { display: block; font-weight: 700; font-size: 0.9rem; color: #10b981; }
  &__out { display: block; font-weight: 600; font-size: 0.76rem; color: #ef4444; }
  &__sub { display: block; font-size: 0.72rem; color: #b9b9c3; white-space: nowrap; }

  /* Sales table star metric: BIG gold FTD pill, dominant over the money */
  &__ftd-hero {
    display: inline-flex;
    align-items: baseline;
    gap: 0.3rem;
    padding: 0.18rem 0.8rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
    color: #fff;
    font-weight: 800;
    font-size: 1.18rem;
    line-height: 1.25;
    letter-spacing: -0.01em;
    box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.6);
    small {
      font-size: 0.62rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.9;
    }
  }
  /* money stays visible but clearly secondary to the FTD pill */

  /* fake money line under the retention amounts — always visible, muted at 0 */
  &__fake-sub {
    display: block;
    margin-top: 0.15rem;
    font-weight: 700;
    font-size: 0.72rem;
    letter-spacing: 0.02em;
    color: #e87722;
    white-space: nowrap;

    &.is-zero { opacity: 0.45; }
  }

  &__money-sub {
    display: block;
    margin-top: 0.2rem;
    font-weight: 600;
    font-size: 0.76rem;
    color: #10b981;
    opacity: 0.85;
  }

  /* clickable leads/clients-received number → assignment list modal */
  &__leads-link {
    cursor: pointer;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    transition: color 0.15s ease;
    &:hover { color: #007BFF; }
    &:focus { outline: none; color: #007BFF; }
  }
  &__received {
    display: inline-flex;
    align-items: center;
    margin-left: 0.45rem;
    padding: 0.14rem 0.6rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #2f7fd6, #4da3ff);
    color: #fff;
    font-weight: 800;
    font-size: 0.74rem;
    text-decoration: none;
    box-shadow: 0 3px 8px -3px rgba(47, 127, 214, 0.6);
    &:hover, &:focus { background: linear-gradient(135deg, #2a72c1, #3d96f5); color: #fff; }
  }

  &__chevron {
    flex-shrink: 0; color: #b9b9c3; font-size: 0.8rem;
    opacity: 0; transform: translateX(-4px);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
}

.agent-board--dark {
  .agent-row {
    &:hover { background: rgba(123, 175, 255, 0.1); }
    &__name { color: #d0d2d6; }
    &__rank.is-plain { color: #8b93ab; background: rgba(255, 255, 255, 0.06); }
    &__bar { background: rgba(255, 255, 255, 0.08); }
    &__sub { color: #7c869c; }
    &__funnel { color: #8b93ab; strong { color: #d0d2d6; } }
    &__leads-link:hover, &__leads-link:focus { color: #8fc1ff; }
    &__received { background: linear-gradient(135deg, #2f7fd6, #4da3ff); color: #fff; &:hover, &:focus { background: linear-gradient(135deg, #3d96f5, #5fb0ff); color: #fff; } }
    &__money-sub { color: #6bdcb1; }
    &__fake-sub { color: #ffb054; }
  }
}
</style>
